import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ShieldAlert } from 'lucide-react';
import type { CopilotoConfig } from '@/api/installation.api';
import * as copilotoApi from '@/api/copiloto.api';
import * as usersApi from '@/api/users.api';
import { canalesDeEntrada, usoLargo } from '@/lib/copiloto-acceso';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { agregarAlias, quitarAlias } from '@/lib/catalogo';
import { EntradaDeFichas } from './EntradaDeFichas';

/**
 * Pestaña COPILOTO (B1).
 *
 * Dos cosas viven acá y NO son lo mismo, y ahora la pantalla las separa con un
 * encabezado que dice cómo se guarda cada una:
 *
 *   - La CONFIGURACIÓN (switch, identidad, umbrales) es parte del borrador de
 *     la pestaña: se edita y se guarda con el botón "Guardar Copiloto".
 *   - El DIRECTORIO se guarda fila por fila, con su propio botón. Es una lista
 *     de personas con acceso a los datos del negocio: un cambio que se quedó en
 *     un borrador que nadie guardó es un problema de verdad, no una molestia.
 *
 * Los campos de identidad llegan VACÍOS a propósito. Cómo se llama el agente y
 * cómo describe el negocio son palabras del dueño; ponerlas por él sería
 * ponerle palabras en la boca.
 */

/** Texto visible de cada rol. El valor guardado es el de la base. */
const ROLES = [
  { value: 'equipo', label: 'Equipo — solo lo operativo de su trabajo' },
  { value: 'dueno', label: 'Dueño — ve todo, incluida la plata' },
];

/** Fila en edición. `id` ausente = alta. */
type Borrador = copilotoApi.UsuarioCopilotoInput;

export function CopilotoTab({
  value,
  onChange,
  isInternal,
}: {
  value: CopilotoConfig;
  onChange: (patch: Partial<CopilotoConfig>) => void;
  /**
   * Instalación interna de Silva Notion. Decide si se MUESTRA el selector de
   * motor: el server exige lo mismo para usarlo, y mostrar una opción que el
   * server va a ignorar es la peor pantalla posible — el dueño la elige, la
   * guarda, y sigue pagando tokens sin que nada se lo explique.
   */
  isInternal: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState<Borrador | null>(null);
  const [porBorrar, setPorBorrar] = useState<copilotoApi.UsuarioCopiloto | null>(null);

  const directorio = useQuery({
    queryKey: ['copiloto', 'usuarios'],
    queryFn: copilotoApi.listarUsuarios,
  });

  // Los usuarios del panel para atar a una persona. Solo al abrir el formulario:
  // mientras está cerrado no hacen falta.
  const usuariosDelPanel = useQuery({
    queryKey: ['users', 'list', 'activos'],
    queryFn: () => usersApi.listUsers({ take: 100, isActive: true }),
    enabled: editando !== null,
  });
  const opcionesDeUsuario = [
    { value: '', label: 'Sin usuario — solo por WhatsApp' },
    ...(usuariosDelPanel.data?.items ?? []).map((u) => ({
      value: u.id,
      label: u.name ? `${u.name} · ${u.email}` : u.email,
    })),
  ];

  const guardar = useMutation({
    mutationFn: copilotoApi.guardarUsuario,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['copiloto', 'usuarios'] });
      setEditando(null);
      toast.success('Persona guardada');
    },
    // El mensaje del server dice DE QUIÉN es el teléfono repetido: mostrarlo tal
    // cual es más útil que un "no se pudo guardar".
    onError: (e: Error) => toast.error(e.message),
  });

  const borrar = useMutation({
    mutationFn: copilotoApi.eliminarUsuario,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['copiloto', 'usuarios'] });
      setPorBorrar(null);
      toast.success('Persona quitada del directorio');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const usuarios = directorio.data ?? [];
  const activos = usuarios.filter((u) => u.activo).length;

  // Lo que falta para que encenderlo tenga sentido. No bloquea el switch —el
  // dueño manda— pero lo dice antes de que se pregunte por qué no funciona.
  const faltantes = [
    value.nombreAgente.trim() === '' && 'el nombre del asistente',
    value.nombreDueno.trim() === '' && 'cómo llamarlo a usted',
    activos === 0 && 'al menos una persona en el directorio',
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-4">
      {/* ---------------------- SECCIÓN 1 · CONFIGURACIÓN ----------------------
          Todo esto es parte del borrador de la pestaña y viaja con el botón
          «Guardar Copiloto». Cada sección dice cómo se guarda: antes convivían
          dos formas de guardar explicadas en una línea suelta, y era fácil
          irse creyendo que estaba todo guardado. */}
      <EncabezadoDeSeccion
        titulo="Configuración"
        comoSeGuarda="Se guarda con el botón «Guardar Copiloto» que aparece abajo al tocar algo."
      />

      {/* ------------------------- EL SWITCH MAESTRO ------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Asistente interno por WhatsApp</CardTitle>
          <CardDescription>
            Contesta por el <strong>mismo número</strong> que atiende a sus clientes. Quién le
            habla lo decide el número: si está en el directorio de abajo, le contesta el
            asistente interno; si no, le contesta la asesora de siempre, igual que hoy.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-slate-200 p-3">
            <Toggle
              checked={value.activo}
              onChange={(activo) => onChange({ activo })}
              label="Encender el asistente interno"
            />
            <div className="text-sm">
              <p className="font-medium text-slate-700">
                {value.activo ? 'Encendido' : 'Apagado'}
              </p>
              <p className="text-slate-500">
                {value.activo
                  ? 'Las personas del directorio hablan con el asistente interno.'
                  : 'Apagado, nadie lo alcanza: todos los mensajes van a la asesora, como hoy.'}
              </p>
            </div>
          </div>

          {value.activo && faltantes.length > 0 && (
            <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Está encendido pero todavía falta {faltantes.join(', ')}. Hasta completarlo va a
                contestar genérico o no va a reconocer a nadie.
              </span>
            </p>
          )}
        </CardBody>
      </Card>

      {/* ----------------------------- EL MOTOR ------------------------------
          Solo en instalaciones internas. Ver la prop `isInternal`.           */}
      {isInternal && (
        <Card>
          <CardHeader>
            <CardTitle>Con qué se paga</CardTitle>
            <CardDescription>
              De dónde sale la inferencia del asistente. No cambia lo que sabe hacer.
            </CardDescription>
          </CardHeader>
          <CardBody className="space-y-3">
            <Select
              label="Motor"
              value={value.motor}
              options={[
                { value: 'api', label: 'Clave de Anthropic — se paga por uso' },
                { value: 'cli', label: 'Suscripción de Silva Notion — no consume la clave' },
              ]}
              onChange={(e) => onChange({ motor: e.target.value as CopilotoConfig['motor'] })}
            />
            <p className="text-xs text-slate-500">
              Con la suscripción, los turnos del asistente no descuentan del tope de gasto de IA
              ni compiten con la asesora que atiende clientes. Si el puente no está disponible,
              el asistente responde igual por la clave: nunca se queda mudo.
            </p>
          </CardBody>
        </Card>
      )}

      {/* --------------------------- LA IDENTIDAD --------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Cómo se presenta</CardTitle>
          <CardDescription>
            Estos campos llegan vacíos a propósito: son sus palabras, no las nuestras.
          </CardDescription>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-2">
          <Input
            label="Cómo se llama el asistente"
            value={value.nombreAgente}
            placeholder="El nombre que usted quiera"
            onChange={(e) => onChange({ nombreAgente: e.target.value })}
          />
          <Input
            data-guia="copiloto.como-lo-llama"
            label="Cómo lo llama a usted"
            value={value.nombreDueno}
            placeholder="Su nombre, o como prefiera que le hable"
            onChange={(e) => onChange({ nombreDueno: e.target.value })}
          />
          <div className="md:col-span-2">
            <Textarea
              label="El negocio en una frase"
              rows={2}
              value={value.descripcionNegocio}
              placeholder="Lo que usted le diría a alguien que pregunta a qué se dedica"
              onChange={(e) => onChange({ descripcionNegocio: e.target.value })}
            />
          </div>
        </CardBody>
      </Card>

      {/* ---------------------------- LOS FRENOS ---------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Frenos</CardTitle>
          <CardDescription>
            Cuándo el asistente tiene que pedir permiso antes de hacer algo, y qué no le enseña a
            quien no es usted.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Input
              label="Un permiso vence a los… (minutos)"
              type="number"
              min={1}
              max={60}
              value={String(value.minutosAprobacion)}
              onChange={(e) => onChange({ minutosAprobacion: Number(e.target.value) })}
            />
            <Input
              label="Un envío es masivo desde… (personas)"
              type="number"
              min={1}
              value={String(value.umbralMasivo)}
              onChange={(e) => onChange({ umbralMasivo: Number(e.target.value) })}
            />
            <Input
              label="Nota de voz máxima (segundos)"
              type="number"
              min={1}
              max={300}
              value={String(value.maxSegundosNota)}
              onChange={(e) => onChange({ maxSegundosNota: Number(e.target.value) })}
            />
          </div>

          <div>
            <Input
              data-guia="copiloto.frase"
              label="Frase para autorizar algo delicado"
              value={value.fraseConfirmacion}
              placeholder="La frase exacta que usted va a escribir"
              onChange={(e) => onChange({ fraseConfirmacion: e.target.value })}
            />
            <p className="mt-1 text-xs text-slate-500">
              Elija una frase que no diría por accidente: un "ok" o un "sí" se contestan sin
              leer. <strong>Mientras esté vacía, el asistente no puede hacer nada delicado</strong>
              {' '}— solo consultar. Eso es a propósito.
            </p>
          </div>

          <EntradaDeFichas
            label="Lo que el equipo NO puede ver"
            valores={value.datosRestringidos}
            onAgregar={(texto) =>
              onChange({ datosRestringidos: agregarAlias(value.datosRestringidos, texto) })
            }
            onQuitar={(texto) =>
              onChange({ datosRestringidos: quitarAlias(value.datosRestringidos, texto) })
            }
            placeholder="facturacion"
            ayuda="Escriba y apriete Enter para agregar cada una. Al dueño no lo limita nada de esta lista."
          />

          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div className="flex items-start gap-3">
              <Toggle
                checked={value.audioSaliente}
                onChange={(audioSaliente) => onChange({ audioSaliente })}
                label="Contestar con notas de voz"
              />
              <div className="text-sm">
                <p className="font-medium text-slate-700">Contestar con notas de voz</p>
                <p className="text-slate-500">
                  Apagado, contesta por escrito. Enciéndalo solo después de probar el asistente
                  escrito: el envío de audio es lo más delicado del módulo, y un audio no se
                  puede deshacer.
                </p>
              </div>
            </div>

            {value.audioSaliente && (
              <div className="grid gap-3 md:grid-cols-2">
                <Input
                  label="Clave del servicio de voz"
                  type="password"
                  value={value.vozApiKey}
                  placeholder="Sin clave, contesta por escrito"
                  onChange={(e) => onChange({ vozApiKey: e.target.value })}
                />
                <Input
                  label="Voz"
                  value={value.vozId}
                  placeholder="El identificador de la voz elegida"
                  onChange={(e) => onChange({ vozId: e.target.value })}
                />
                <p className="text-xs text-slate-500 md:col-span-2">
                  Si falta cualquiera de los dos, o el servicio no responde, el asistente contesta
                  por escrito. Nunca se queda callado por culpa del audio.
                </p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* ------------------- SECCIÓN 2 · QUIÉN LE PUEDE HABLAR -----------------
          Se guarda PERSONA POR PERSONA, en el momento. Es la lista de quién
          puede pedirle datos del negocio: un cambio que se quedó en un borrador
          que nadie guardó no es una molestia, es un problema de verdad. */}
      <EncabezadoDeSeccion
        titulo="Quién le puede hablar"
        comoSeGuarda="Cada persona se guarda al momento, con su propio botón. No depende de «Guardar Copiloto»."
      />

      <Card>
        <CardHeader>
          <CardTitle>Quién le puede hablar</CardTitle>
          <CardDescription>
            Esta lista es lo único que separa a su equipo de un cliente cualquiera sobre el mismo
            número.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {directorio.isLoading && <Spinner />}

          {!directorio.isLoading && usuarios.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              El directorio está vacío. Mientras lo esté, el asistente interno no reconoce a
              nadie —ni a usted— y todos los mensajes van a la asesora.
            </p>
          )}

          {usuarios.map((u) => (
            <div
              key={u.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 p-3"
            >
              <div className="min-w-0 text-sm">
                <p className="font-medium text-slate-700">
                  {u.nombre}
                  <span className="font-normal text-slate-400">
                    {' · '}
                    {u.rol === 'dueno' ? 'Dueño' : 'Equipo'}
                  </span>
                  {!u.activo && (
                    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                      desactivado
                    </span>
                  )}
                  {/* Por dónde le habla: con usuario, desde el panel; con teléfono, por WhatsApp. */}
                  {canalesDeEntrada(u).map((canal) => (
                    <span
                      key={canal}
                      className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 text-[11px] font-normal text-brand-700"
                    >
                      {canal}
                    </span>
                  ))}
                </p>
                <p className="text-slate-500">
                  {[u.telefono, u.usuario?.email, u.alcance].filter(Boolean).join(' · ')}
                </p>
                <p className="text-xs text-slate-400">{usoLargo(u.mensajes30d ?? 0)}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    // Solo lo que se guarda: el uso y el usuario son de lectura.
                    setEditando({
                      id: u.id,
                      telefono: u.telefono ?? '',
                      userId: u.userId,
                      nombre: u.nombre,
                      rol: u.rol,
                      alcance: u.alcance,
                      activo: u.activo,
                    })
                  }
                >
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Quitar a ${u.nombre}`}
                  onClick={() => setPorBorrar(u)}
                >
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            variant="secondary"
            size="sm"
            data-guia="copiloto.agregar-persona"
            onClick={() =>
              setEditando({ telefono: '', userId: null, nombre: '', rol: 'equipo', alcance: '' })
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Agregar persona
          </Button>
        </CardBody>
      </Card>

      {/* ---------------------- alta / edición de persona ---------------------- */}
      <Modal
        open={editando !== null}
        onClose={() => setEditando(null)}
        title={editando?.id ? 'Editar persona' : 'Agregar persona'}
        description="El teléfono se guarda limpio: puede escribirlo como quiera."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              loading={guardar.isPending}
              onClick={() => editando && guardar.mutate(editando)}
            >
              Guardar persona
            </Button>
          </div>
        }
      >
        {editando && (
          <div className="space-y-3">
            <Select
              label="Usuario del panel (opcional)"
              value={editando.userId ?? ''}
              options={opcionesDeUsuario}
              onChange={(e) => setEditando({ ...editando, userId: e.target.value || null })}
            />
            <Input
              label="Nombre"
              value={editando.nombre ?? ''}
              placeholder={
                editando.userId
                  ? 'Si lo dejas vacío, se usa el nombre del usuario'
                  : 'Cómo lo va a saludar el asistente'
              }
              onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
            />
            <div>
              <Input
                label="Teléfono de WhatsApp (opcional)"
                value={editando.telefono ?? ''}
                placeholder="809 555 1234"
                onChange={(e) => setEditando({ ...editando, telefono: e.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Sin usuario: solo por WhatsApp. Sin teléfono: solo desde el panel.
              </p>
            </div>
            <Select
              label="Qué puede ver"
              value={editando.rol}
              options={ROLES}
              onChange={(e) =>
                setEditando({ ...editando, rol: e.target.value as 'dueno' | 'equipo' })
              }
            />
            <Input
              label="De qué se encarga (opcional)"
              value={editando.alcance ?? ''}
              placeholder="Instalaciones de Santiago"
              onChange={(e) => setEditando({ ...editando, alcance: e.target.value })}
            />
            {editando.id && (
              <div className="flex items-center gap-3 rounded-md border border-slate-200 p-3">
                <Toggle
                  checked={editando.activo !== false}
                  onChange={(activo) => setEditando({ ...editando, activo })}
                  label="Puede hablarle al asistente"
                />
                <p className="text-sm text-slate-600">
                  {editando.activo === false
                    ? 'Desactivado: sigue en la lista pero el asistente no lo reconoce.'
                    : 'Puede hablarle al asistente.'}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={porBorrar !== null}
        onClose={() => setPorBorrar(null)}
        title={`¿Quitar a ${porBorrar?.nombre ?? ''} del directorio?`}
        description="Deja de reconocerlo de inmediato. Si solo quiere pausarlo, edítelo y desactívelo: así queda en la lista."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPorBorrar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={borrar.isPending}
              onClick={() => porBorrar && borrar.mutate(porBorrar.id)}
            >
              Quitar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          {porBorrar?.telefono ?? porBorrar?.usuario?.email ?? 'Sin WhatsApp'} ·{' '}
          {porBorrar?.rol === 'dueno' ? 'Dueño' : 'Equipo'}
        </p>
      </Modal>
    </div>
  );
}

/**
 * Separador de las dos mitades de la pestaña, diciendo CÓMO se guarda cada una.
 *
 * Las dos formas de guardar convivían sin marca visible: se explicaban en una
 * línea de la descripción de una tarjeta, y quien no la leía se iba creyendo
 * que había guardado todo.
 */
function EncabezadoDeSeccion({ titulo, comoSeGuarda }: { titulo: string; comoSeGuarda: string }) {
  return (
    <div className="border-b border-surface-border pb-2 pt-2 dark:border-slate-800">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {titulo}
      </h3>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{comoSeGuarda}</p>
    </div>
  );
}
